import { DATE_FORMATS, IMAGE_TYPES } from "@booking/consts";
import { type DatePlannerDatumType } from "@booking/types";
import clsx from "clsx";
import {
  endOfDay,
  format as formatDate,
  formatISO,
  startOfDay,
} from "date-fns";
import React from "react";

import { type SourceProps } from "@/components/atoms/media/Source";
import type { CommonProps, ImageMetric } from "@/types";

import { gaDialogShown } from "./tracking";

function addInvalidDate(date: Date) {
  return {
    date,
    disabled: true,
    duration: 0,
  } as DatePlannerDatumType;
}

function areDatesUnique(rounds: Array<any>) {
  const dates = rounds.map(({ date }) => formatDate(date, "yyyy-MM-dd"));

  return dates.length === new Set(dates).size;
}

function camelize(str: string): string {
  return str.toLowerCase().replace(/[-|_]./g, ([_, s]) => s.toUpperCase());
}

function closeDialog(e: any) {
  const { target } = e;
  const button: HTMLButtonElement = target.closest("button");
  const dialog: HTMLDialogElement = target.closest("dialog");

  if ((button && !button.dataset.action) || target === dialog) {
    // click on close button or backdrop
    dialog.close();
  }
}

function cloudinarySource(
  src: string,
  mediaWidth: number,
  metrics: ImageMetric,
): Array<SourceProps> {
  const {
    height,
    width,
    ar = height && height === width ? "1:1" : undefined,
  } = metrics;
  const params = ["q_80"];

  if (ar) {
    params.push("c_auto", `ar_${ar}`);
  } else {
    params.push("c_fill");
    if (height) {
      params.push(`h_${height * 2}`);
    }
  }

  params.push(`w_${width * 2}`);

  const srcset = src
    .replace("upload/", `upload/${params.join(",")}/`)
    .replace(/\.webp$/, ".jpg"); // in case a WebP reference was sent

  return IMAGE_TYPES.map((imageType) => {
    const sourceProps: SourceProps = { srcset };

    if (mediaWidth > 0) {
      sourceProps.media = `(min-width: ${mediaWidth}px)`;
    }

    if (imageType !== "jpg") {
      sourceProps.srcset = srcset.replace(/\.jpg$/, ".webp");
      sourceProps.type = `image/${imageType}`;
    }

    return sourceProps;
  });
}

function contentfulSource(
  src: string,
  mediaWidth: number,
  metrics: ImageMetric,
): Array<SourceProps> {
  const { height, width } = metrics;
  const paramsArray = [`w=${width}`];

  if (height) {
    paramsArray.push(`h=${height}`, "fit=scale");
  }

  const params = paramsArray.join("&");

  return IMAGE_TYPES.map((imageType) => {
    return {
      media: mediaWidth > 0 ? `(min-width: ${mediaWidth}px)` : null,
      srcset: `${src}?fm=${imageType}&${params}`,
      type: imageType === "jpg" ? null : `image/${imageType}`,
    } as SourceProps;
  });
}

function datasetFlag(condition: boolean) {
  return condition ? "" : null;
}

function dayInterval(date: Date) {
  const start = startOfDay(date);
  const end = endOfDay(date);

  return { start, end };
}

function filterEntries(
  obj: Record<any, any>,
  fn: (arr: [any, any]) => boolean,
): Record<any, any> {
  return Object.fromEntries(Object.entries(obj).filter(fn));
}

function flatMapEntries(
  obj: Record<any, any>,
  fn: (arr: [any, any]) => Array<[any, any]>,
): Record<any, any> {
  return Object.fromEntries(Object.entries(obj).flatMap(fn));
}

function getEnumKeyByEnumValue<
  TEnumKey extends string,
  TEnumVal extends string | number,
>(enumObject: { [key in TEnumKey]: TEnumVal }, enumValue: TEnumVal) {
  const keys = (Object.keys(enumObject) as Array<TEnumKey>).filter(
    (x) => enumObject[x] === enumValue,
  );

  return keys.length > 0 ? keys[0] : undefined;
}

function groupBy<T>(arr: Array<T>, fn: (item: T) => any) {
  return arr.reduce(
    (groups, curr) => {
      (groups[fn(curr)] ||= []).push(curr);

      return groups;
    },
    {} as Record<string, Array<T>>,
  );
}

function isoDate(date: Date): string {
  return formatISO(date, { representation: "date" });
}

function mapEntries(
  obj: Record<any, any>,
  fn: (arr: [any, any]) => [any, any],
): Record<any, any> {
  return Object.fromEntries(Object.entries(obj).map(fn));
}

function openDialog(id: string, eventGroup?: string) {
  const dialog = document.getElementById(id) as HTMLDialogElement;

  if (dialog) {
    dialog.showModal();
    gaDialogShown({ eventGroup, id });
  }
}

const ordinalDayShortMonth = (date: Date) =>
  formatDate(date, DATE_FORMATS.ORDINAL_DAY_SHORT_MONTH);

function renderChildren(id: string, children: React.ReactNode) {
  const items = React.Children.toArray(children);

  if (items.length === 0) {
    return null;
  }

  return items.map((child, index) => (
    <React.Fragment key={`${id}_${index}`}>{child}</React.Fragment>
  ));
}

function renderChildrenWithProps(
  id: string,
  children: React.ReactNode,
  props: any,
) {
  const items = React.Children.toArray(children);

  if (items.length === 0) {
    return null;
  }

  return items
    .map((child, index) => {
      if (child == null) {
        return child;
      }

      const className = clsx(
        props.className,
        (child as React.ReactElement<CommonProps>).props.className,
      );

      return (
        <React.Fragment key={`${id}_${index}`}>
          {React.cloneElement(child as React.ReactElement, {
            ...props,
            className,
          })}
        </React.Fragment>
      );
    })
    .filter(Boolean);
}

function selectedDatesToString(selectedDates: Array<Date>) {
  return selectedDates.map((date) => ordinalDayShortMonth(date)).join(" - ");
}

function underscore(str: string): string {
  return str
    .split(/(?=[A-Z])/)
    .join("_")
    .toLowerCase();
}

function underscoreKeys(obj: object): object {
  return mapEntries(obj, ([k, v]) => {
    let value = v;

    if (Array.isArray(v)) {
      value = v.map(underscoreKeys);
    } else if (typeof v === "object") {
      value = underscoreKeys(v);
    }

    return [underscore(k), value];
  });
}

export {
  addInvalidDate,
  areDatesUnique,
  camelize,
  closeDialog,
  cloudinarySource,
  contentfulSource,
  datasetFlag,
  dayInterval,
  filterEntries,
  flatMapEntries,
  getEnumKeyByEnumValue,
  groupBy,
  isoDate,
  mapEntries,
  openDialog,
  ordinalDayShortMonth,
  renderChildren,
  renderChildrenWithProps,
  selectedDatesToString,
  underscore,
  underscoreKeys,
};
